// LivePrices.tsx
import React, { useState, useEffect } from 'react';
import { Sparkles, BarChart, Gem, Scale, Save } from 'lucide-react'; 

const LivePrices: React.FC = () => {
  // States for prices
  const [gold24KPrice, setGold24KPrice] = useState<string>("");
  const [gold22KPrice, setGold22KPrice] = useState<string>("");
  const [gold18KPrice, setGold18KPrice] = useState<string>("");
  const [silverPriceKg, setSilverPriceKg] = useState<string>("");
  
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Page load hone par loading dikhane ke liye
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  // 1. Page Load hote hi Database se data fetch karna (GET)
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        // Apna backend URL yahan check kar lena, mostly 8080 par hi chalta hai
        // const response = await fetch('http://localhost:8080/api/metal-prices');
        const response = await fetch('https://sardar-jewellers.com/api/metal-prices');
        if (response.ok) {
          const data = await response.json();
          setGold24KPrice(data.gold24K.toString());
          setGold22KPrice(data.gold22K.toString());
          setGold18KPrice(data.gold18K.toString());
          setSilverPriceKg(data.silverKg.toString());
        } else {
          console.error("Failed to fetch prices");
        }
      } catch (error) {
        console.error("Network error fetching prices:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrices();
  }, []);

  // Bonus Feature: 24K change hone par 22K aur 18K auto-calculate
  const handle24KChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setGold24KPrice(val);
    
    if(val && !isNaN(Number(val))) {
        setGold22KPrice((Number(val) * (22 / 24)).toFixed(2));
        setGold18KPrice((Number(val) * (18 / 24)).toFixed(2));
    }
  };

  // 2. Data save karna Database mein (PUT)
  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    setIsError(false);
    
    try {
      // const response = await fetch('http://localhost:8080/api/metal-prices', {
      const response = await fetch('https://sardar-jewellers.com/api/metal-prices', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gold24K: parseFloat(gold24KPrice),
          gold22K: parseFloat(gold22KPrice),
          gold18K: parseFloat(gold18KPrice),
          silverKg: parseFloat(silverPriceKg)
        })
      });

      if (response.ok) {
        setSaveMessage("✅ Prices updated successfully on the website!");
      } else {
        setIsError(true);
        setSaveMessage("❌ Failed to update prices. Please try again.");
      }
    } catch (error) {
      console.error("Error saving prices:", error);
      setIsError(true);
      setSaveMessage("❌ Network error. Backend is not running.");
    } finally {
      setIsSaving(false);
      // Message ko 3 second baad gayab karne ke liye
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-100">
        <h2 className="text-2xl font-bold text-gray-700">Loading Latest Prices...</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8 flex flex-col items-center">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full my-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <div>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 flex items-center">
                <Sparkles className="w-8 h-8 text-yellow-500 mr-3" />
                Manage Market Prices
                </h1>
                <p className="text-gray-600 text-lg">
                Update daily prices for Gold (per gram) and Silver (per 1 kg).
                </p>
            </div>
            
            <button 
                onClick={handleSave}
                disabled={isSaving}
                className={`mt-4 md:mt-0 flex items-center px-6 py-3 rounded-lg text-white font-bold transition-all shadow-md ${
                    isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 hover:shadow-lg'
                }`}
            >
                {isSaving ? (
                     <span className="flex items-center">
                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                         Saving...
                     </span>
                ) : (
                    <><Save className="w-5 h-5 mr-2" /> Save Updates</>
                )}
            </button>
        </div>

        {/* Success / Error Message Alert */}
        {saveMessage && (
            <div className={`mb-6 p-4 border-l-4 font-medium rounded-r-md ${
              isError ? 'bg-red-50 border-red-500 text-red-700' : 'bg-green-50 border-green-500 text-green-700'
            }`}>
                {saveMessage}
            </div>
        )}

        {/* Gold Prices Section */}
        <div className="mb-10 mt-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <Gem className="w-8 h-8 text-yellow-600 mr-3" />
            Gold Prices (per gram)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* 24K Gold Card */}
            <div className="bg-yellow-100 p-6 rounded-lg shadow-md border-2 border-yellow-300 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-xl sm:text-2xl font-bold text-yellow-800 mb-3 text-center">24 Karat (99.9% Pure)</h3>
              <div className="flex items-center text-4xl font-extrabold text-yellow-700 mb-4">
                <span className="mr-1">₹</span>
                <input 
                    type="number" 
                    value={gold24KPrice} 
                    onChange={handle24KChange}
                    className="w-full max-w-[140px] bg-white/50 px-2 py-1 rounded outline-none border-b-2 border-yellow-400 focus:bg-white focus:border-yellow-600 transition-colors text-center"
                />
              </div>
              <span className="text-sm text-gray-600">Pure Gold</span>
            </div>

            {/* 22K Gold Card */}
            <div className="bg-yellow-50 p-6 rounded-lg shadow-md border-2 border-yellow-200 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-xl sm:text-2xl font-bold text-yellow-700 mb-3 text-center">22 Karat (91.6% Pure)</h3>
              <div className="flex items-center text-4xl font-extrabold text-yellow-600 mb-4">
                <span className="mr-1">₹</span>
                <input 
                    type="number" 
                    value={gold22KPrice} 
                    onChange={(e) => setGold22KPrice(e.target.value)}
                    className="w-full max-w-[140px] bg-white/50 px-2 py-1 rounded outline-none border-b-2 border-yellow-300 focus:bg-white focus:border-yellow-500 transition-colors text-center"
                />
              </div>
              <span className="text-sm text-gray-600">Jewellery Gold</span>
            </div>

            {/* 18K Gold Card */}
            <div className="bg-yellow-50 p-6 rounded-lg shadow-md border-2 border-yellow-100 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-xl sm:text-2xl font-bold text-yellow-600 mb-3 text-center">18 Karat (75% Pure)</h3>
              <div className="flex items-center text-4xl font-extrabold text-yellow-500 mb-4">
                <span className="mr-1">₹</span>
                <input 
                    type="number" 
                    value={gold18KPrice} 
                    onChange={(e) => setGold18KPrice(e.target.value)}
                    className="w-full max-w-[140px] bg-white/50 px-2 py-1 rounded outline-none border-b-2 border-yellow-200 focus:bg-white focus:border-yellow-400 transition-colors text-center"
                />
              </div>
              <span className="text-sm text-gray-600">Durable Jewellery</span>
            </div>

          </div>
        </div>

        {/* Silver Prices Section */}
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <Scale className="w-8 h-8 text-blue-600 mr-3" />
            Silver Prices (per 1 kg)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Silver Price Card */}
            <div className="bg-blue-100 p-6 rounded-lg shadow-md border-2 border-blue-300 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-2xl font-bold text-blue-800 mb-3 text-center">Fine Silver (99.9% Pure)</h3>
              <div className="flex items-center text-4xl font-extrabold text-blue-700 mb-4">
                <span className="mr-1">₹</span>
                <input 
                    type="number" 
                    value={silverPriceKg} 
                    onChange={(e) => setSilverPriceKg(e.target.value)}
                    className="w-full max-w-[160px] bg-white/50 px-2 py-1 rounded outline-none border-b-2 border-blue-400 focus:bg-white focus:border-blue-600 transition-colors text-center"
                />
              </div>
              <span className="text-sm text-gray-600">Industrial & Investment Use</span>
            </div>

            {/* Placeholder / Extra Card */}
            <div className="bg-blue-50 p-6 rounded-lg shadow-md border-2 border-blue-200 flex flex-col items-center justify-center text-gray-500 italic transition-transform transform hover:scale-105">
              <BarChart className="w-12 h-12 mb-3 text-gray-400" />
              <p className="text-xl">Additional price data coming soon!</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LivePrices;